import { Context, Plugin } from '@segment/analytics-next';
import { loadScript } from '@segment/analytics-next/src/lib/load-script';
import { CONFIG } from '../../config';
import { logger } from '../logger';
import { OPENAI_ADS_SDK_URL, hashEmail, mapSegmentTrackToOpenAiAdsEvent } from './openAiAdsEvents';

const pixelId = () => CONFIG.OPENAI_ADS_PIXEL_ID;

let identifiedEmailHash: string | null = null;

const installOaiqQueue = () => {
  if (window.oaiq) return;
  const queue = function () {
    // eslint-disable-next-line prefer-rest-params
    queue.q?.push(arguments);
  } as NonNullable<Window['oaiq']>;
  queue.q = [];
  window.oaiq = queue;
};

const oaiq = (...args: unknown[]) => window.oaiq?.(...args);

const load = () => {
  installOaiqQueue();
  oaiq('init', { pixelId: pixelId() });
  loadScript(OPENAI_ADS_SDK_URL).catch((error: unknown) => {
    const message = error instanceof Error ? error.message : String(error);
    logger.info('OPENAI_ADS_SCRIPT_LOAD_FAILED', message, { error });
  });
  return Promise.resolve();
};

const setIdentifiedEmail = (email: unknown) => {
  const emailHash = typeof email === 'string' && email.trim() !== '' ? hashEmail(email) : null;

  if (emailHash && emailHash !== identifiedEmailHash) {
    oaiq('init', { pixelId: pixelId(), user: { email_sha256: emailHash } });
  } else if (!emailHash && identifiedEmailHash) {
    oaiq('init', { pixelId: pixelId(), user: {} });
  }
  identifiedEmailHash = emailHash;
};

const identify = (context: Context) => {
  setIdentifiedEmail(context.event.traits?.email);
  return context;
};

const page = (context: Context) => {
  oaiq('measure', 'page_viewed', { type: 'contents' });
  return context;
};

const track = (context: Context) => {
  const { event } = context;
  if (event.event == null) return context;

  if (event.event === 'Signed Out') {
    setIdentifiedEmail(null);
    return context;
  }

  const mapped = mapSegmentTrackToOpenAiAdsEvent(event.event, event.properties);
  if (mapped == null) return context;

  if (mapped.eventId) {
    oaiq('measure', mapped.name, mapped.data, { event_id: mapped.eventId });
  } else {
    oaiq('measure', mapped.name, mapped.data);
  }

  return context;
};

export const createOpenAiAdsPixel = (): Plugin => ({
  name: 'OpenAI Ads Pixel',
  type: 'destination',
  version: '1.0.0',
  load,
  isLoaded: () => true,
  identify,
  page,
  track
});
